/* Pinterest browser bridge — Phase D.4 of the conversion-tracking plan.
 *
 * Listens to the dataLayer events published by conversion-tracker.js
 * and translates to Pinterest Tag's `pintrk('track', eventName, data)`
 * format. Maps GA4-style events:
 *
 *   view_item        → 'pagevisit'  (Pinterest's product-view event)
 *   add_to_cart      → 'addtocart'
 *   begin_checkout   → 'checkout'   (Pinterest's intent-to-checkout
 *                       event; NOT the actual purchase completion —
 *                       that's server-side via the Worker, per D.R8)
 *   view_cart        → (not mapped — Pinterest doesn't have a cart-
 *                       view event analog)
 *   remove_from_cart → (not mapped)
 *
 * Purchase events come EXCLUSIVELY from the server-side Pinterest CAPI
 * call (yuki-backend/src/services/pinterest-converter.ts D.8). The
 * browser-side `checkout` event from begin_checkout is a separate
 * "intent" signal — Pinterest's optimization model uses both.
 *
 * Loaded by theme.liquid AFTER conversion-tracker.js + AFTER the
 * Pinterest Tag base snippet (which exposes `window.pintrk`). Skipped
 * silently when `window.pintrk` isn't initialized.
 */

(function () {
  if (typeof window === 'undefined') return;
  if (typeof window.pintrk !== 'function') {
    console.debug('[pinterest-tracker] Pinterest Tag base not present; skipping bridge');
    return;
  }
  if (!window.dataLayer || !Array.isArray(window.dataLayer)) {
    console.warn('[pinterest-tracker] dataLayer not initialized; conversion-tracker.js must load first');
    return;
  }

  // ── line_items[] builder shared by add_to_cart + checkout ──────────
  function toLineItems(items) {
    return (items || [])
      // Skip constituent prints (we report the Set parent as a single
      // item to Pinterest; constituents would double-count).
      .filter(function (i) { return !(i.is_set_item && i.set_position); })
      .map(function (i) {
        return {
          product_id: i.item_id,
          product_name: i.item_name,
          product_price: i.price,
          product_quantity: i.quantity || 1,
          product_category: i.item_category,
        };
      });
  }

  function handleViewItem(event) {
    const item = event.ecommerce && event.ecommerce.items && event.ecommerce.items[0];
    if (!item) return;
    window.pintrk('track', 'pagevisit', {
      line_items: [{
        product_id: item.item_id,
        product_name: item.item_name,
        product_price: item.price,
        product_quantity: 1,
        product_category: item.item_category,
      }],
    });
  }

  function handleAddToCart(event) {
    const items = (event.ecommerce && event.ecommerce.items) || [];
    const lineItems = toLineItems(items);
    if (lineItems.length === 0) return;
    const quantity = lineItems.reduce(function (s, l) { return s + (l.product_quantity || 1); }, 0);
    window.pintrk('track', 'addtocart', {
      value: event.ecommerce && event.ecommerce.value,
      order_quantity: quantity,
      currency: (event.ecommerce && event.ecommerce.currency) || 'USD',
      line_items: lineItems,
    });
  }

  function handleBeginCheckout(event) {
    const items = (event.ecommerce && event.ecommerce.items) || [];
    const lineItems = toLineItems(items);
    if (lineItems.length === 0) return;
    const quantity = lineItems.reduce(function (s, l) { return s + (l.product_quantity || 1); }, 0);
    window.pintrk('track', 'checkout', {
      value: event.ecommerce && event.ecommerce.value,
      order_quantity: quantity,
      currency: (event.ecommerce && event.ecommerce.currency) || 'USD',
      line_items: lineItems,
    });
  }

  // ── dataLayer subscriber ───────────────────────────────────────────

  const originalPush = window.dataLayer.push.bind(window.dataLayer);

  function handle(evt) {
    if (!evt || typeof evt !== 'object' || !evt.event) return;
    try {
      switch (evt.event) {
        case 'view_item':      handleViewItem(evt);      break;
        case 'add_to_cart':    handleAddToCart(evt);     break;
        case 'begin_checkout': handleBeginCheckout(evt); break;
        // view_cart, remove_from_cart, identify: not mapped
      }
    } catch (err) {
      console.warn('[pinterest-tracker] handler threw for', evt.event, err);
    }
  }

  if (!window.dataLayer.__pinterestWrapped) {
    window.dataLayer.push = function () {
      const result = originalPush.apply(window.dataLayer, arguments);
      for (let i = 0; i < arguments.length; i++) handle(arguments[i]);
      return result;
    };
    window.dataLayer.__pinterestWrapped = true;
  }

  // Drain existing events.
  for (let i = 0; i < window.dataLayer.length; i++) handle(window.dataLayer[i]);
})();
